import { CheckoutProps } from '@wix/checkout-components';
import { useCheckoutStore } from '../../../../providers';
import {
  BiReporter,
  CheckoutBiLogParams,
  DoublePurchaseInfo,
  FedopsReporter,
  Product,
  SentryReporter,
} from '../../../../types';
import {
  convertDoublePurchaseStatusToCode,
  DoublePurchaseValidationResult,
  DynamicDoublePurchaseValidationResult,
  getDoublePurchaseInfoDefaultValue,
  PaymentCycle,
  PaymentCycle_BIMap,
  ProductTypeIds,
} from '../../../../utils';
import {
  CheckoutApi,
  CheckoutUrls,
  ClassicDoublePurchaseRequest,
  ClassicDoublePurchaseResponse,
} from '../../../../Api/checkout';
import { ApiManager } from '../../../../Api';

interface DoublePurchaseBiParams {
  productTypeId: string;
  conflictingServiceGuid: string;
  reason: string;
  productGuid: string | undefined;
}

interface DynamicDoublePurchaseParams {
  siteGuid: string;
  isUpgrade: boolean;
  logError: (errorMsg: Error) => void;
}

interface ClassicDoublePurchaseParams {
  siteGuid: string;
  isUpgrade: boolean;
  currentProduct: Product;
  currentCycle: PaymentCycle;
  eventAggregator: string;
  logError: (errorMsg: Error) => void;
}

interface DoublePurchaseResult {
  isDoublePurchase: boolean;
  doublePurchaseInfo?: Partial<DoublePurchaseInfo>;
}
export interface UseOnBeforeSubmitOptions {
  currentProduct: Product;
  currentCycle: PaymentCycle;
  isDynamicOffering: boolean;
  siteGuid: string;
  isUpgrade: boolean;
  doublePurchaseInfoDefaultValue?: DoublePurchaseInfo;
  submitPurchaseClickedBiEventLog: BiReporter<CheckoutBiLogParams>;
  doublePurchasePopupViewedLog: BiReporter<DoublePurchaseBiParams>;
  beginSubmitOrderPageInteraction: FedopsReporter;
  endSubmitOrderPageInteraction: FedopsReporter;
  onDoublePurchase: (doublePurchaseInfo: DoublePurchaseInfo) => void;
  logError: SentryReporter;
}

async function isDynamicDoublePurchase({
  siteGuid,
  isUpgrade,
  logError,
}: DynamicDoublePurchaseParams): Promise<DoublePurchaseResult> {
  const defaultResult = { isDoublePurchase: false };
  try {
    if (isUpgrade) {
      return defaultResult;
    }
    const { status } = await CheckoutApi.verifyDynamicDoublePurchase(siteGuid);
    const isDoublePurchase =
      status === DynamicDoublePurchaseValidationResult.DELIVERED ||
      status === DynamicDoublePurchaseValidationResult.PENDING;
    if (isDoublePurchase) {
      return {
        isDoublePurchase,
        doublePurchaseInfo: {
          validationCode: convertDoublePurchaseStatusToCode(status),
        },
      };
    }
    return {
      isDoublePurchase,
    };
  } catch (e: any) {
    logError(e);
  }
  return defaultResult;
}

async function isClassicDoublePurchase({
  siteGuid,
  isUpgrade,
  currentProduct,
  currentCycle,
  eventAggregator,
  logError,
}: ClassicDoublePurchaseParams): Promise<DoublePurchaseResult> {
  const defaultResult = { isDoublePurchase: false };
  try {
    const { success, existingPackageInfo, validationCode } =
      await ApiManager.post<
        ClassicDoublePurchaseRequest,
        ClassicDoublePurchaseResponse
      >(CheckoutUrls.VERIFY_DOUBLE_PURCHASE, {
        siteGuid,
        eventAggregator,
        appDefId: 0,
        upgrade: isUpgrade,
        paymentCycle: currentCycle,
        productId: currentProduct.id,
      });
    const isDoublePurchase = !(
      success ||
      !existingPackageInfo ||
      validationCode === DoublePurchaseValidationResult.GENERAL_ERROR
    );
    if (isDoublePurchase) {
      return {
        isDoublePurchase,
        doublePurchaseInfo: {
          info: existingPackageInfo,
          validationCode,
        },
      };
    }
    return { isDoublePurchase };
  } catch (e: any) {
    logError(e);
  }
  return defaultResult;
}

function getDoublePurchaseBiParams(
  doublePurchaseInfo: DoublePurchaseInfo,
  productGuid?: string,
): DoublePurchaseBiParams {
  const conflictingServiceGuid =
    doublePurchaseInfo.info?.conflictingServiceGuid ?? '';
  const reason =
    doublePurchaseInfo.validationCode ===
    DoublePurchaseValidationResult.ALREADY_DELIVERED
      ? 'already-delivered'
      : 'pending';
  return {
    productGuid,
    productTypeId: ProductTypeIds.PremiumPlans,
    conflictingServiceGuid,
    reason,
  };
}

export function useOnBeforeSubmit(
  options?: UseOnBeforeSubmitOptions,
): CheckoutProps['onBeforeSubmit'] | undefined {
  const { state } = useCheckoutStore();
  if (options) {
    const {
      currentProduct,
      currentCycle,
      siteGuid,
      isUpgrade,
      isDynamicOffering,
      logError,
      submitPurchaseClickedBiEventLog,
      doublePurchasePopupViewedLog,
      beginSubmitOrderPageInteraction,
      endSubmitOrderPageInteraction,
      onDoublePurchase,
      doublePurchaseInfoDefaultValue = getDoublePurchaseInfoDefaultValue(),
    } = options;
    const productGuid = currentProduct.guid;
    const biParams = {
      productGuid,
      productId: currentProduct.id,
      eventAggregator: state.orderPageEventAggregator,
      cycle: PaymentCycle_BIMap[currentCycle] ?? 0,
    };
    return async () => {
      submitPurchaseClickedBiEventLog(biParams);
      beginSubmitOrderPageInteraction();
      const { isDoublePurchase, doublePurchaseInfo } = isDynamicOffering
        ? await isDynamicDoublePurchase({
            siteGuid,
            isUpgrade,
            logError,
          })
        : await isClassicDoublePurchase({
            siteGuid,
            isUpgrade,
            currentProduct,
            currentCycle,
            eventAggregator: state.orderPageEventAggregator,
            logError,
          });
      const newDoublePurchaseInfo = {
        ...doublePurchaseInfoDefaultValue,
        ...doublePurchaseInfo,
      };
      if (isDoublePurchase) {
        endSubmitOrderPageInteraction();
        onDoublePurchase(newDoublePurchaseInfo);
        doublePurchasePopupViewedLog(
          getDoublePurchaseBiParams(newDoublePurchaseInfo, productGuid),
        );
        return false;
      } else {
        return true;
      }
    };
  }

  return undefined;
}
